/**
 * Studio — build a pack, launch it as a coin.
 *
 * A teaser for something that does not exist yet, which is the whole reason
 * this file is careful. Two rules it holds to:
 *
 *   Nothing here is a control. There is no form, no waitlist field and no
 *   button that pretends to do something — an input that silently discards
 *   what you type is worse than no input, and a "reserve your spot" on a
 *   feature with no backend is a lie with a cursor on it. Every CTA points at
 *   something that genuinely works today.
 *
 *   No invented numbers. A creator split, a launch fee or a date written here
 *   would be read as a commitment the moment it shipped, and none of those are
 *   decided. Where a figure would go, the page says it is not set yet. That is
 *   less impressive and it is the only version that stays true.
 *
 * The one hard number on the page is the RTP floor, and it is read off the
 * generated catalogue rather than retyped, so it cannot drift from the floor
 * the shop actually sells.
 */
import { Link } from 'react-router-dom'

import { catalogue } from '@exlyr/shared'

import { SectionHeading } from '../components/ui'

/**
 * What a creator would and would not control. Kept as data because the split
 * between the two is the actual product decision on this page: everything in
 * `yours` is taste, everything in `ours` is the part that makes a pack
 * checkable, and that boundary cannot move without the fairness page becoming
 * a lie.
 */
const YOURS = [
  {
    title: 'The contents',
    body: 'Pick the tokens that can come out of it, from anything the market page already lists.',
  },
  {
    title: 'The shape',
    body: 'How many cards, how the value is spread across them, and how fat the tail gets.',
  },
  {
    title: 'The print',
    body: 'Name it, colour it, and put your own art on the wrapper.',
  },
]

const OURS = [
  {
    title: 'The odds are published',
    body: 'Every pack ships its full odds table and its return-to-player before anyone can buy it. Yours would be no exception.',
  },
  {
    title: 'The roll is committed',
    body: 'Same commit-reveal scheme every pack on the site already uses. You do not get to see the server seed either.',
  },
  {
    title: 'There is a floor',
    body: 'A published RTP below the house minimum is not a pack anyone is allowed to sell.',
  },
]

export function StudioPage() {
  // The lowest published RTP on the ladder today, taken off the generated
  // catalogue rather than typed here, so the floor this page quotes is the
  // floor the shop actually sells and cannot drift from it.
  const floorRtp = Math.min(...catalogue().map((pack) => pack.rtp))

  return (
    <div className="py-6">
      <SectionHeading
        title="Studio"
        sub="Build your own pack. Launch it as a coin. Earn every time somebody rips it."
      />

      <Hero />

      <section className="mt-12">
        <h2 className="text-xl font-semibold tracking-tight">How it would work</h2>
        <p className="mt-1 max-w-2xl text-sm" style={{ color: 'var(--color-muted)' }}>
          Three steps, and the middle one is the part that does not exist anywhere else.
        </p>
        <Steps />
      </section>

      <section className="mt-12 grid gap-5 lg:grid-cols-2 lg:items-start">
        <Panel
          eyebrow="Yours to decide"
          title="You design the pack"
          items={YOURS}
          accent="var(--color-brand)"
        />
        <Panel
          eyebrow="Not yours to decide"
          title="The rules travel with it"
          items={OURS}
          accent="var(--color-muted)"
          note={`Whatever you build publishes its odds the same way every tier does today. The floor is currently ${(floorRtp * 100).toFixed(1)}%, and a pack under it cannot ship.`}
        />
      </section>

      <Unsettled />
      <Disclosure />
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * Hero
 * ------------------------------------------------------------------ */

function Hero() {
  return (
    <div className="panel relative overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(80% 60% at 18% 0%, color-mix(in srgb, var(--color-brand) 12%, transparent) 0%, transparent 64%)',
        }}
        aria-hidden
      />

      <div className="relative grid gap-8 p-6 sm:p-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
        <div>
          <span
            className="inline-flex items-center gap-2 border px-2.5 py-1 text-[0.6875rem] font-bold tracking-[0.16em] uppercase"
            style={{
              borderColor: 'color-mix(in srgb, var(--color-brand) 45%, var(--color-line))',
              color: 'var(--color-brand)',
              background: 'var(--color-brand-wash)',
            }}
          >
            In development
          </span>

          <h1 className="mt-4 text-3xl leading-[1.05] font-bold tracking-tight sm:text-5xl">
            Anyone can be the house
          </h1>

          <p
            className="mt-4 max-w-lg text-sm leading-relaxed"
            style={{ color: 'var(--color-muted)' }}
          >
            Today there are six packs and we wrote all of them. Studio opens that up: assemble a
            pack out of real tokens, publish its odds, and put it in the shop under your own name.
            Each one launches with a coin of its own, and you keep a cut of every rip.
          </p>

          <p className="mt-3 max-w-lg text-xs" style={{ color: 'var(--color-faint)' }}>
            None of this is live yet, and there is no date. When it is, it will show up here.
          </p>

          <div className="mt-7 flex flex-wrap gap-2.5">
            <Link to="/packs" className="btn btn-primary btn-lg">
              See the packs we built
            </Link>
            <Link to="/fairness" className="btn btn-ghost btn-lg">
              How the odds work
            </Link>
          </div>
        </div>

        <div className="hidden lg:block">
          <div className="mx-auto w-full max-w-[460px]">
            <LaunchDiagram />
          </div>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * The diagram: a pack becoming a coin, and the loop back.
 * ------------------------------------------------------------------ */

const W = 400
const H = 300

function LaunchDiagram() {
  const accent = 'var(--color-brand)'
  const packX = 78
  const coinX = 322
  const midY = 122

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" role="img" aria-label="A pack launching as a coin">
      {/* The pack. */}
      <g>
        <rect
          x={packX - 38}
          y={midY - 54}
          width="76"
          height="108"
          rx="2"
          fill="var(--color-surface-2)"
          stroke={accent}
          strokeOpacity="0.55"
        />
        <rect x={packX - 38} y={midY - 54} width="76" height="13" fill={accent} fillOpacity="0.28" />
        <circle cx={packX} cy={midY - 6} r="17" fill="none" stroke={accent} strokeOpacity="0.5" />
        <circle cx={packX} cy={midY - 6} r="8" fill={accent} fillOpacity="0.45" />
        <rect x={packX - 24} y={midY + 24} width="48" height="4" fill={accent} fillOpacity="0.35" />
        <rect x={packX - 16} y={midY + 34} width="32" height="3" fill="#ffffff" fillOpacity="0.16" />
      </g>
      <text
        x={packX}
        y={midY + 76}
        fontSize="12"
        fontWeight="700"
        textAnchor="middle"
        fill="#ffffff"
        fillOpacity="0.75"
      >
        Your pack
      </text>

      {/* The arrow across. */}
      <path
        d={`M${packX + 52} ${midY - 6} H${coinX - 56}`}
        stroke={accent}
        strokeWidth="2"
        strokeDasharray="6 5"
        fill="none"
      />
      <path
        d={`M${coinX - 64} ${midY - 13} L${coinX - 54} ${midY - 6} L${coinX - 64} ${midY + 1}`}
        fill="none"
        stroke={accent}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text
        x={(packX + coinX) / 2}
        y={midY - 20}
        fontSize="11"
        fontWeight="600"
        textAnchor="middle"
        fill={accent}
      >
        launches
      </text>

      {/* The coin. */}
      <circle cx={coinX} cy={midY - 6} r="42" fill="var(--color-surface-2)" stroke={accent} strokeOpacity="0.6" />
      <circle cx={coinX} cy={midY - 6} r="32" fill="none" stroke={accent} strokeOpacity="0.3" />
      <text
        x={coinX}
        y={midY + 1}
        fontSize="20"
        fontWeight="800"
        textAnchor="middle"
        fill={accent}
      >
        $
      </text>
      <text
        x={coinX}
        y={midY + 76}
        fontSize="12"
        fontWeight="700"
        textAnchor="middle"
        fill="#ffffff"
        fillOpacity="0.75"
      >
        Its coin
      </text>

      {/* The loop back: rips pay the creator. */}
      <path
        d={`M${coinX} ${midY + 46} C ${coinX} ${H - 34}, ${packX} ${H - 34}, ${packX} ${midY + 46}`}
        fill="none"
        stroke={accent}
        strokeWidth="2"
        strokeOpacity="0.75"
      />
      <path
        d={`M${packX - 7} ${midY + 56} L${packX} ${midY + 46} L${packX + 7} ${midY + 56}`}
        fill="none"
        stroke={accent}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <text
        x={(packX + coinX) / 2}
        y={H - 22}
        fontSize="11"
        fontWeight="600"
        textAnchor="middle"
        fill={accent}
      >
        every rip pays you
      </text>
    </svg>
  )
}

/* ------------------------------------------------------------------ *
 * Steps
 * ------------------------------------------------------------------ */

const STEPS = [
  {
    n: 1,
    title: 'Build it',
    body: 'Choose the tokens, the card count and the value curve. The odds table writes itself as you go, so you can see what you are actually selling before anyone can buy it.',
  },
  {
    n: 2,
    title: 'Launch it',
    body: 'Publishing mints a coin for the pack. It trades on its own, and it is the thing people hold if they believe your pack is worth opening.',
  },
  {
    n: 3,
    title: 'Get paid',
    body: 'Every rip of your pack routes a share back to you. The more it gets opened, the more the coin behind it has to point at.',
  },
]

function Steps() {
  return (
    <div className="mt-5 grid gap-4 md:grid-cols-3">
      {STEPS.map((step) => (
        <div key={step.n} className="panel p-5">
          <div
            className="grid size-8 place-items-center text-sm font-bold"
            style={{ background: 'var(--color-brand-wash)', color: 'var(--color-brand)' }}
          >
            {step.n}
          </div>
          <h3 className="mt-3.5 text-base font-semibold">{step.title}</h3>
          <p className="mt-1.5 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
            {step.body}
          </p>
        </div>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 * The two halves
 * ------------------------------------------------------------------ */

function Panel({
  eyebrow,
  title,
  items,
  accent,
  note,
}: {
  eyebrow: string
  title: string
  items: Array<{ title: string; body: string }>
  accent: string
  note?: string
}) {
  return (
    <section className="panel p-5">
      <div className="eyebrow" style={{ color: accent }}>
        {eyebrow}
      </div>
      <h2 className="mt-1 text-base font-semibold">{title}</h2>

      <dl className="mt-4 space-y-4">
        {items.map((item) => (
          <div key={item.title} className="border-t pt-3.5">
            <dt className="text-sm font-semibold">{item.title}</dt>
            <dd className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
              {item.body}
            </dd>
          </div>
        ))}
      </dl>

      {note && (
        <p className="mt-4 border-t pt-3.5 text-[0.6875rem] leading-relaxed" style={{ color: 'var(--color-faint)' }}>
          {note}
        </p>
      )}
    </section>
  )
}

/* ------------------------------------------------------------------ *
 * What is not decided
 * ------------------------------------------------------------------ */

const OPEN_QUESTIONS = [
  ['Creator share', 'What fraction of a rip goes to whoever built the pack.'],
  ['Cost to launch', 'Whether publishing costs anything, and what it costs.'],
  ['Who can publish', 'Whether this opens to everyone at once or starts with a smaller group.'],
  ['Ship date', 'Not set. This page will say so when it changes.'],
]

function Unsettled() {
  return (
    <section className="panel mt-12 p-5">
      <div className="eyebrow">Still open</div>
      <h2 className="mt-1 text-base font-semibold">Things we have not decided</h2>
      <p className="mt-1.5 max-w-2xl text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
        Listed rather than guessed at. A number written here would be read as a promise the moment
        it was published, and none of these have been settled.
      </p>

      <dl className="mt-5 grid gap-x-8 gap-y-4 sm:grid-cols-2">
        {OPEN_QUESTIONS.map(([term, detail]) => (
          <div key={term} className="border-t pt-3">
            <dt className="flex items-baseline justify-between gap-3 text-sm font-semibold">
              <span>{term}</span>
              <span className="eyebrow shrink-0">Not set</span>
            </dt>
            <dd className="mt-1 text-xs leading-relaxed" style={{ color: 'var(--color-muted)' }}>
              {detail}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  )
}

/* ------------------------------------------------------------------ *
 * Disclosure
 * ------------------------------------------------------------------ */

function Disclosure() {
  return (
    <section className="panel mt-5 p-5">
      <div className="eyebrow">Read this part too</div>
      <div
        className="mt-3 grid gap-6 text-xs leading-relaxed lg:grid-cols-2"
        style={{ color: 'var(--color-muted)' }}
      >
        <p>
          Everything on this page describes work that has not shipped. It is a statement of
          intent, not a specification, and not a commitment to build any particular part of it.
          Features described here may change or may never exist.
        </p>
        <p>
          Nothing here is an offer, a solicitation, or a recommendation to buy anything, and a coin
          attached to a pack would not be an investment in Elyxr or in whoever created that pack.
          Packs are a negative-expectation product, which is as true of one you build as one we
          did. <Link to="/fairness" className="underline">The odds page</Link> explains exactly how
          that is published and checked.
        </p>
      </div>
    </section>
  )
}
